import { DashboardDataTransformer } from '../../data_transformers/dashboard_data_transformer';
import { DashboardFilter } from '../../filters/dashboard_filter';
import { Granularity, Metric } from '../dashboard-config.model';
import { KpiOption, kpisOfMetric, selectedKpi } from '../kpis/kpi-options';

export type PeriodKind = 'year' | 'quarter' | 'month';

/**
 * Time resolution of the metric's datasets. The metric decides year vs period;
 * the analysis decides single value vs range.
 */
export type MetricTemporal =
  | { kind: 'year' }
  | {
      kind: 'period';
      periodKinds: readonly PeriodKind[];
      /** Latest selectable year relative to the newest data year (Tourism lags one year). */
      maxYearOffset?: number;
      /**
       * With the period type on `year`, rows take their year as `period_id` (Employed,
       * `Q2ToYearPeriodIdDashboardDataTransformer`).
       */
      yearAsPeriodId?: boolean;
    };

interface MetricFilterOverride<TOptions> {
  granularities: Granularity[];
  options: TOptions;
}

/**
 * Metric-owned copy: nouns and chrome labels, not full analysis headings.
 */
export interface MetricLabels {
  /** Metric name in the sidebar and the bottom-up cards: 'Unternehmen' | 'Umsätze' | … */
  label?: string;
  /** Unit next to a count in the bottom-up cards. Default 'Anzahl'. */
  countUnit?: string;
  /** e.g. 'ANZAHL AN' | 'UMSÄTZE VON' | 'BRUTTOWERTSCHÖPFUNG' */
  headingPhrase: string;
  /** e.g. 'Anzahl' | 'Umsatz' | 'Bruttowertschöpfung' — metricLabel / industries */
  shortLabel: string;
  /** e.g. 'Unternehmen' | 'Umsatz' — dossier / industries metricName */
  entityLabel?: string;
  /** Popup chrome for the type-select field */
  typeFilterTitle?: string;
  /** NumDE unit when non-empty */
  displayUnit?: string;
  /** Noun forms for dossier headings and slice titles; a KPI may override them (`KpiOption.entityLabels`). */
  entity?: MetricEntityLabels;
}

/** Noun forms of what a metric (or KPI) counts, e.g. Beschäftigtenstruktur / der Beschäftigten / Beschäftigte. */
export interface MetricEntityLabels {
  /** e.g. Unternehmensstruktur. */
  structureLabel: string;
  /** Form following the genitive article, e.g. Beschäftigten. */
  entityAfterDerLabel: string;
  /** Genitive article preceding entityAfterDerLabel. Defaults to “der”. */
  genitiveArticle?: string;
  /** Standalone form, e.g. Beschäftigte. Falls back to entityAfterDerLabel. */
  standaloneEntityLabel?: string;
}

/**
 * Metric type-select filter: fixed factory + optional ctor args per axis.
 * Axis options never replace the filter class.
 */
export interface MetricFilterConfig<TOptions = unknown> {
  /** filterValues Map key / DashboardFilter.name */
  name: string;
  /** Row key written by filterValueLabelTransformers. */
  valueLabelKey: string;
  /** Metric factory function signature */
  create: (options?: TOptions) => DashboardFilter<any>[];
  /** Default options used on factory for all granularities unless overridden. */
  options?: TOptions;
  /** Optional overrides for specific granularities. */
  overrides?: MetricFilterOverride<TOptions>[];
  /**
   * The selected option names the metric itself (GVA: Bruttowertschöpfung /
   * Produktionswert) rather than a sub-type of it, so headings and popups show
   * the option label in place of the metric label.
   */
  selectsMetric?: boolean;
}

export interface MetricTransformerFactoryConfig {
  create: () => DashboardDataTransformer[];
}

export interface MetricProfileConfig {
  id: Metric;
  /** Metric segment of dataset names (`ds__txd__companies_count__…`). */
  datasetToken: string;
  labels: MetricLabels;
  /** Defaults to yearly data. */
  temporal?: MetricTemporal;
  filter?: MetricFilterConfig<any>;
  filterValueLabelTransformers?: MetricTransformerFactoryConfig;
}

/** Exposed filter identity on the resolved profile. */
export interface MetricProfileFilter {
  name: string;
  valueLabelKey: string;
  selectsMetric: boolean;
}

export interface MetricProfile {
  id: Metric;
  /** Metric segment of dataset names (`ds__txd__companies_count__…`). */
  datasetToken: string;
  labels: MetricLabels;
  temporal: MetricTemporal;
  /** The metric's KPIs from KPI_OPTIONS, in default order. */
  kpis: () => KpiOption[];
  /** Row key for the metric/type display label (`filter.valueLabelKey`). */
  valueLabelKey: string;
  /** Present when the metric has a type-select filter. */
  filter?: MetricProfileFilter;
  /** Metric select filter(s), if this family has one. */
  createMetricFilters: (granularityId?: Granularity) => DashboardFilter<any>[];
  /** Filter-value → display-name transformers. */
  createFilterValueLabelTransformers: () => DashboardDataTransformer[];
  /**
   * Metric phrase + live type label from filterValues, when applicable.
   * Analysis verbs (`RANKING DER`, `ENTWICKLUNG DER`) stay analysis-local.
   */
  formatHeadingPhrase: (filterValues: Map<string, any>) => string;
  /**
   * KPI-filter counterpart of formatHeadingPhrase, with article, for analysis
   * leads: `DER ANZAHL AN Gesamtbeschäftigten`, `DER BRUTTOWERTSCHÖPFUNG`.
   * Reads the raw `kpi` value, so no heading-word mapping is needed.
   */
  formatKpiGenitivePhrase: (filterValues: Map<string, any>) => string;
  /** Nominative label of the selected KPI for map headings, e.g. `Gesamtbeschäftigte`. */
  formatKpiLabel: (filterValues: Map<string, any>) => string;
  /** Genitive/plural word of the selected KPI for headings, e.g. `Gesamtbeschäftigten`. */
  formatKpiHeadingLabel: (filterValues: Map<string, any>) => string;
  /** Article-less genitive of the selected KPI for counts, e.g. `Gesamtbeschäftigter` ("ANZAHL …"). */
  formatKpiGenitiveLabel: (filterValues: Map<string, any>) => string;
}

/**
 * Define a metric profile.
 */
export function defineMetricProfile({
  id,
  datasetToken,
  labels,
  temporal = { kind: 'year' },
  filter,
  filterValueLabelTransformers,
}: MetricProfileConfig): MetricProfile {
  /* ==========================================================================
   * Value Label
   * ========================================================================== */

  const valueLabelKey = filter?.valueLabelKey ?? '';

  /* ==========================================================================
   * Filter
   * ========================================================================== */

  const profileFilter: MetricProfileFilter | undefined = filter
    ? {
        name: filter.name,
        valueLabelKey: filter.valueLabelKey,
        selectsMetric: filter.selectsMetric ?? false,
      }
    : undefined;

  const createMetricFilters = (granularity?: Granularity) => {
    if (!filter) {
      return [];
    }

    const options = filter.overrides
      ?.filter(
        (override) =>
          granularity !== undefined &&
          override.granularities.includes(granularity),
      )
      .reduce(
        (mergedOptions, override) => ({
          ...mergedOptions,
          ...override.options,
        }),
        { ...(filter.options ?? {}) },
      );

    return filter.create(options ?? filter.options);
  };

  /* ==========================================================================
   * Filter Value Transformer
   * ========================================================================== */

  const createFilterValueLabelTransformers = () => {
    if (!filterValueLabelTransformers) {
      return [];
    }

    return filterValueLabelTransformers.create();
  };

  const formatHeadingPhrase = (filterValues: Map<string, any>) => {
    if (!profileFilter) {
      return labels.headingPhrase;
    }

    const typeLabel = filterValues.get(profileFilter.name);
    if (profileFilter.selectsMetric) {
      return typeLabel || labels.headingPhrase;
    }
    return typeLabel
      ? `${labels.headingPhrase} ${typeLabel}`
      : labels.headingPhrase;
  };

  const kpis = () => kpisOfMetric(id);

  /** Selected KPI when it belongs to this metric and the metric offers a choice. */
  const choosableKpi = (filterValues: Map<string, any>) => {
    const kpi = selectedKpi(filterValues);
    return kpi?.metricId === id && kpis().length > 1 ? kpi : undefined;
  };

  const formatKpiGenitivePhrase = (filterValues: Map<string, any>) => {
    const kpi = choosableKpi(filterValues);
    if (!kpi) {
      return `DER ${labels.headingPhrase}`;
    }
    return kpi.headingPhrase ?? `DER ${labels.headingPhrase} ${kpi.headingLabel}`;
  };

  const formatKpiLabel = (filterValues: Map<string, any>) =>
    choosableKpi(filterValues)?.label ?? kpis()[0]?.label ?? '';

  const formatKpiHeadingLabel = (filterValues: Map<string, any>) =>
    choosableKpi(filterValues)?.headingLabel ?? kpis()[0]?.headingLabel ?? '';

  const formatKpiGenitiveLabel = (filterValues: Map<string, any>) => {
    const kpi = choosableKpi(filterValues) ?? kpis()[0];
    return kpi?.genitiveLabel ?? kpi?.headingLabel ?? '';
  };

  return {
    id,
    datasetToken,
    labels,
    temporal,
    kpis,
    valueLabelKey,
    filter: profileFilter,
    createMetricFilters,
    createFilterValueLabelTransformers,
    formatHeadingPhrase,
    formatKpiGenitivePhrase,
    formatKpiLabel,
    formatKpiHeadingLabel,
    formatKpiGenitiveLabel,
  };
}
